import { AgTechQueryBuilder, queryResult, useAgTechOptionalQuery, useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries'
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface'
import AgTechWebAppStripeContext from '@agtech/web/components/App/Stripe/AgTechWebAppStripeContext'
import { AgTechButton, AgTechButtonProps, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons'
import AgTechSelectableCard from '@agtech/web/components/Cards/AgTechSelectableCard'
import AgTechHeaderDetail from '@agtech/web/components/Headers/AgTechHeaderDetail'
import { useModalContext } from '@agtech/web/components/Portals/Modals/AgTechModal'
import { AgTechMoneyFormatter } from '@agtech/web/utilities/AgTechUtilities'
import { SaleOperationDataGetLicensedProductsQuery } from '@sales/data/operation/SaleDataOperationQueries'
import { Sale } from '@sales/data/sales/SaleData'
import { SaleDataGetSalePaymentSecretQuery, SaleDataIsSaleLicensedQuery, SalePurchaseDataDTO } from '@sales/data/sales/SaleDataQueries'
import { PaymentElement } from '@stripe/react-stripe-js'
import React, { useEffect } from 'react'

type SalePaymentModalProps = {
    sale: Sale,
    purchaseIntent?: SalePurchaseDataDTO,
    callbacks?: {
        onSuccessfulPayment?: () => Promise<void>
    }
}

type SalePaymentModalSteps = 'Selection' | 'Checkout';

const SalePaymentModal = (props: SalePaymentModalProps) => {
    const [productId, setProductId] = React.useState<number | undefined>(
        props.purchaseIntent?.product?.id ?? undefined
    );

    const [currentStep, setCurrentStep] = React.useState<SalePaymentModalSteps>(
        productId ? 'Checkout' : 'Selection'
    );

    return currentStep === 'Selection' ? (
        <SalePaymentModalSelectionPage
            {...props}
            onProductSelected={async (productId) => {
                setProductId(productId);
                setCurrentStep('Checkout');
            }}
        />
    ) : (
        <SalePaymentModalCheckoutPage
            {...props}
            productId={productId!}
        />
    )
}

const SalePaymentModalSelectionPage = (props: SalePaymentModalProps & {
    onProductSelected: (productId: number) => Promise<void>
}) => {
    const [productId, setProductId] = React.useState<number | undefined>(undefined);

    const productsQuery = useAgTechQuery({
        query: SaleOperationDataGetLicensedProductsQuery
    });

    return (
        <div className='h-[420px] w-[480px] border rounded bg-white p-4 flex'>
            <AgTechQueryLoadingSurface query={productsQuery}>
                {(products) => (
                    <div className='flex-col stretched flex'>
                        <AgTechHeaderDetail
                            header='Select a product to purchase'
                            settings={{
                                header: {
                                    classes: '!text-lg !font-semibold mt-1',
                                }
                            }}
                        />
                        <div className='flex-row gap-x-3 wide mt-8 flex-wrap justify-center flex'>
                            {products.map((product, index) => {
                                return (
                                    <AgTechSelectableCard
                                        key={index}
                                        header={product.productName}
                                        detail={product.productDescription}
                                        isSelected={product.id === productId}
                                        onSelected={async () => {
                                            setProductId(product.id);
                                        }}
                                        structure={{ width: 200}}
                                        body={() => (
                                            <div className='flex-col mt-auto pt-2 mb-2 flex'>
                                                <h1 className='text-2xl font-semibold'>
                                                    ${product.cost.toFixed(2)}
                                                </h1>
                                            </div>
                                        )}
                                    />
                                )
                            })}
                        </div>
                        <div className='wide centered mt-auto pb-3 flex'>
                            <AgTechButton
                                text='Proceed to checkout'
                                icon='fas fa-arrow-right'
                                classes='button-highlight mt-4'
                                iconOnLeft={false}
                                action={() => props.onProductSelected(productId!)}
                                disableIf={!productId}
                            />
                        </div>
                    </div>
                )}
            </AgTechQueryLoadingSurface>
        </div>
    )
}

const SalePaymentModalCheckoutPage = (props: SalePaymentModalProps & {
    productId: number
}) => {
    const modalContext = useModalContext();

    useEffect(() => {
        modalContext.growModal('420px', '860px');
    }, []);

    const productInfoQueryProp = AgTechQueryBuilder.buildOptionalQuery({
        query: SaleDataGetSalePaymentSecretQuery,
        data: props.purchaseIntent
    });

    return (
        <AgTechWebAppStripeContext
            publishableKey='pk_test_51Mfqa1Ip2rILbopoMC4utx89a162Dv4zVJq0GVgpVTTQ0nZh1Huql7OlolIiK5fVwISIlecAEJrEB4WaUP2UCLc200aoVreRV2'
            productInfoQuery={{
                query: productInfoQueryProp,
                queryContext: { saleId: props.sale.id, productId: props.productId }
            }}
            confirmPaymentQuery={{
                query: SaleDataIsSaleLicensedQuery,
                queryContext: { saleId: props.sale.id }
            }}
            callbacks={{
                onSuccessfulPayment: async () => {
                    await props.callbacks?.onSuccessfulPayment?.();
                    modalContext.closeModal();
                }
            }}
        >
            {(productInfo) => {
                return (
                    <div className='flex-row stretched flex'>
                        <div className='p-3 flexed overflow-hidden flex'>
                            <div className='flexed overflow-auto flex'>
                                <PaymentElement />
                            </div>
                        </div>
                        <div className='border-l w-[320px] py-4 px-6 bg-gray-100 flex-col flex'>
                            <AgTechHeaderDetail
                                header={`Sale Checkout`}
                                settings={{
                                    header: {
                                        classes: '!text-lg !font-semibold mt-1',
                                    }
                                }}
                            />
                            <div className='mt-8 wide flex-col flex'>
                                <div className='flex-row wide justify-between items-center flex'>
                                    <h1 className='text-less font-semibold'>{productInfo.product.productName}</h1>
                                    <h1 className='text-md font-bold'>{AgTechMoneyFormatter.format(productInfo.product.cost)}</h1>
                                </div>
                            </div>
                            <div className='mt-auto wide flex-row justify-between flex'>
                                <AgTechButton
                                    text='Checkout'
                                    icon='fas fa-check'
                                    classes='button-highlight wide centered py-3'
                                />
                            </div>
                        </div>
                    </div>
                );
            }}
        </AgTechWebAppStripeContext>
    )
}

SalePaymentModal.Open = (e: AgTechClickEventHandler, props: SalePaymentModalProps) => {
    e.modals.showModal({
        content: () => <SalePaymentModal {...props} />
    })
}

SalePaymentModal.Button = (props: AgTechButtonProps & SalePaymentModalProps) => {
    return (
        <AgTechButton
            text='Checkout'
            icon='fas fa-check'
            {...props}
            action={e => SalePaymentModal.Open(e, props)}
        />
    )
}

export default SalePaymentModal